import mapboxgl from 'mapbox-gl';
import { addLayer, removeLayer } from './layers/layer_helpers';
import { addSource, removeSource } from './sources/source_helpers';
import { removeImage, loadAndAddImage } from './image_helpers';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';

const alltrailsGlpyhs = 'mapbox://fonts/alltrails/{fontstack}/{range}.pbf';

const initCustomStyle = map => {
  map.customStyle = {
    cursor: '',
    sources: [],
    layers: [],
    layerEvents: {},
    images: []
  };
};

const injectCustomStyle = map => {
  const { sources, layers, images } = map.customStyle;
  sources.forEach(s => {
    addSource(map, s.sourceId, s.sourceSpec);
  });
  images.forEach(i => {
    loadAndAddImage(map, i.imageId, i.imageSrc, i.opts);
  });
  layers.forEach(layer => {
    addLayer(map, layer);
  });
};

const clearCustomStyle = map => {
  const { sources, layers, images } = map.customStyle;
  // Iterate over shallow array copies to prevent issues
  // with splicing the array currently being iterated over
  layers.slice().forEach(layer => {
    removeLayer(map, layer.id, false);
  });
  images.slice().forEach(i => {
    removeImage(map, i.imageId, false);
  });
  sources.slice().forEach(s => {
    removeSource(map, s.sourceId, false);
  });
};

const setTerrain = (map, enabled) => {
  if (enabled) {
    const currentTerrain = map.getSource('mapbox-terrain');

    if (!currentTerrain) {
      map.addSource('mapbox-terrain', {
        type: 'raster-dem',
        url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
        tileSize: 512,
        maxzoom: 14
      });
    }

    map.setTerrain({ source: 'mapbox-terrain', exaggeration: 1.5 });

    const currentSky = map.getLayer('sky');

    if (!currentSky) {
      map.addLayer({
        id: 'sky',
        type: 'sky',
        paint: {
          'sky-type': 'atmosphere',
          'sky-atmosphere-sun': [0.0, 0.0],
          'sky-atmosphere-sun-intensity': 15,
          'sky-atmosphere-color': 'rgba(85, 151, 210, 0.5)'
        }
      });
    }

    if (map.getLayer('contour-line')) {
      map.setFilter('contour-line', ['!', true]);
      map.setFilter('contour-label', ['!', true]);
    }
  } else {
    map.setTerrain(null).setMaxPitch(60);

    if (map.getLayer('contour-line')) {
      map.setFilter('contour-line', null);
      map.setFilter('contour-label', null);
    }
  }
};

const swapBaseStyle = (map, style, terrainEnabled) => {
  map.setStyle(style);
  map.once('styledata', () => {
    setTerrain(map, terrainEnabled);
    injectCustomStyle(map);
  });
};

// NOTE: Please communicate any chages here to the mobile teams, as the mobile clients use similar regex for localization
const localizeVectorStyle = (style, locale, displayMetric) => {
  // Do nothing if displaying English, imperial layer
  if ((!locale || locale === 'en') && !displayMetric) {
    return style;
  }
  let styleStr = JSON.stringify(style)
    .replace(/name_en/g, `name_${locale}`)
    .replace(/n:en/g, `n:${locale}`)
    .replace(/PRIVATE/g, StringConstantsMixin.strings.PRIVATE_UPPER_CASE);
  if (displayMetric) {
    styleStr = styleStr
      .replace(/elevation_ft/g, 'elevation_m')
      .replace(/ ft/g, ' m')
      .replace(/\["get","l"]/g, '["/",["round",["*",["get","l"],16.1]],10]')
      .replace(/\["\*",\["get","ele"],3\.281]/g, '["get","ele"]') // used in AllTrails layer
      .replace(/\["\*",\["get","ele"],3\.28084]/g, '["get","ele"]'); // used in AllTrails Mapbox Outdoors layer
  }
  style = JSON.parse(styleStr);
  return style;
};

const addAllTrailsGlyphsToStyle = style => {
  // Without this, custom layers using Proxima Nova Font would break
  style.glyphs = alltrailsGlpyhs;
  return style;
};

const loadVectorStyle = (styleId, locale, displayMetric) => {
  if (!locale) {
    locale = 'en';
  }
  return new Promise((resolve, reject) => {
    $.ajax({
      type: 'GET',
      contentType: 'application/json',
      url: `https://api.mapbox.com/styles/v1/${styleId}`,
      data: {
        optimize: locale === 'en' && !displayMetric,
        access_token: mapboxgl.accessToken
      },
      success: style => {
        style = localizeVectorStyle(style, locale, displayMetric);
        style = addAllTrailsGlyphsToStyle(style);
        resolve(style);
      },
      error: e => {
        reject(e);
      }
    });
  });
};

const createBlankStyle = () => {
  return {
    layers: [],
    sources: {},
    glyphs: alltrailsGlpyhs,
    version: 8
  };
};

export { alltrailsGlpyhs, createBlankStyle, initCustomStyle, swapBaseStyle, clearCustomStyle, loadVectorStyle, setTerrain };
